import { Center, Container, Loader, Stack, Text, Title } from '@mantine/core';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useSubscription } from 'urql';
import { isUuid } from '@shared';
import { ErrorAlert } from '@/components/error-alert';
import { readTemplateVariables } from '@/components/template-variable-editor';
import { getErrorMessage } from '@/lib/error-message';
import { isTemplatesEnabled } from '@/lib/features';
import { readOutputHandles } from '@/components/output-handles';
import { PersonaTemplateSubscription, UpdatePersonaTemplateMutation } from './-components/graphql';
import { PersonaTemplateEditor } from './-components/persona-template-editor';
import type { PersonaTemplateValues } from './-components/persona-template-editor';

export const Route = createFileRoute('/_authed/system/persona-templates/$id')({
  beforeLoad: () => {
    if (!isTemplatesEnabled()) throw redirect({ to: '/' });
  },
  component: SystemPersonaTemplatePage,
});

function SystemPersonaTemplatePage() {
  const { id } = Route.useParams();
  const hasValidId = isUuid(id);

  const [{ data, error }] = useSubscription({
    query: PersonaTemplateSubscription,
    variables: { templateId: id },
    pause: !hasValidId,
  });

  if (error) {
    return (
      <Container size="xl" py="xl">
        <ErrorAlert message={getErrorMessage(error, 'Failed to load template')} />
      </Container>
    );
  }

  if (hasValidId && data === undefined) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  const template = data?.personaTemplatesByPk;

  if (!template) {
    return (
      <Center h="50vh">
        <Text c="dimmed">Persona template not found</Text>
      </Center>
    );
  }

  return (
    <EditPersonaTemplate
      key={template.id}
      templateId={template.id}
      name={template.name}
      values={{
        name: template.name,
        description: template.description,
        category: template.category ?? '',
        featured: template.featured,
        sortOrder: template.sortOrder,
        variables: readTemplateVariables(template.variables),
        personaName: template.personaName,
        personaDescription: template.personaDescription,
        instructions: template.instructions,
        greeting: template.greeting,
        maxTurns: template.maxTurns,
        outputHandles: readOutputHandles(template.outputHandles),
      }}
    />
  );
}

type EditPersonaTemplateProps = {
  templateId: string;
  name: string;
  values: PersonaTemplateValues;
};

function EditPersonaTemplate({ templateId, name, values }: EditPersonaTemplateProps) {
  const [, runUpdateTemplate] = useMutation(UpdatePersonaTemplateMutation);

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={1}>Edit Persona Template</Title>
          <Text c="dimmed" mt="xs">
            {name}
          </Text>
        </div>
        <PersonaTemplateEditor
          initialValues={values}
          submitLabel="Save"
          onSubmit={async (next) => {
            const result = await runUpdateTemplate({
              templateId,
              set: { ...next, category: next.category || null },
            });

            return result.error ? getErrorMessage(result.error, 'Failed to save template') : null;
          }}
        />
      </Stack>
    </Container>
  );
}
